import { QueryClient, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from 'react-query';
import { Organisation } from 'src/types/Organisation';
import { get, post, put } from '../utils';
import { Property } from 'src/types/Property';
import { useAggregatedQueries } from 'src/hooks/useAggregatedQuery';
import { getPage, Page, PageRequest, sortValueFrom } from '../pagination';
import { OrganisationSetting } from 'src/types/OrganisationSetting';

/** Every organisation query hangs off this, so one reset reaches all of them. */
export const ORGANISATION_BASE_QUERY_KEY = 'Organisations';
const BASE_QUERY_KEY = ORGANISATION_BASE_QUERY_KEY;
enum OrganisationQueryKeys {
  OrganisationProperties = 'OrganisationProperties'
}

export function getOrganisationById(organisationId: string): Promise<Organisation> {
  return get<Organisation>(`organisation/${organisationId}`);
}

export function getOrganisationsForCustomer(customerId: string): Promise<Organisation[]> {
  return get<Organisation[]>('organisation', { customerId });
}

export function searchOrganisations(name: string): Promise<Organisation[]> {
  return get<Organisation[]>('organisation', { name });
}

export function createOrganisation(name: string, customerId: string, settings?: Pick<OrganisationSetting, 'name' | 'value'>[]): Promise<Organisation> {
  const body = {
    name,
    customerId,
    settings
  };
  return post<Organisation>('organisation', body);
}

/**
 * Renames an organisation. The API only lets `name` change, and it rejects any column it is
 * sent that differs from the stored row — an absent `customerId` counts as a change — so the
 * body carries the unchanged `customerId` alongside the new name.
 */
export function renameOrganisation(organisation: Pick<Organisation, 'id' | 'customerId'>, name: string): Promise<Organisation> {
  const { id, customerId } = organisation;
  return put<Organisation>(`organisation/${id}`, { id, customerId, name });
}

export type MoveOrganisationToCustomerPayload = {
  destinationCustomerId: string;
  userIds?: string[];
  deviceIds?: string[];
  preserveSourceUserCustomer?: boolean;
};

export function moveOrganisationToCustomer(
  organisationId: string,
  payload: MoveOrganisationToCustomerPayload
): Promise<any> {
  return post<any>(`organisation/${organisationId}/move-to-customer`, payload);
}

export function useQueryOrganisationsForCustomer(customerId: string): UseQueryResult<Organisation[]> {
  return useQuery([BASE_QUERY_KEY, customerId], () => getOrganisationsForCustomer(customerId));
}

/**
 * One page of an organisation's properties, searched on the SERVER.
 *
 * Its caller is a property picker on the device CSV import. That picker was a dropdown
 * built from every property in the organisation — first one unpaged request, then every
 * page walked — which on 1,500 properties is a menu nobody can use and a payload nobody
 * needs. It is a type-to-search box now, and the search is a WHERE clause.
 */
export function useSearchOrganisationProperties(
  organisationId: string,
  search: string,
  options?: UseQueryOptions<Page<Property>>
): UseQueryResult<Page<Property>> {
  return useQuery(
    [BASE_QUERY_KEY, organisationId, OrganisationQueryKeys.OrganisationProperties, search],
    () =>
      getPage<Property>(
        'installation',
        { limit: 50, offset: 0, search: search || undefined },
        { organisationId },
        {
          // The legacy branch (`searchPropertiesInternal`) reads organisationId,
          // customPropertyId, id, address1 and tag — never `search`. Without this the
          // picker would list the organisation's first fifty properties whatever was
          // typed, and the operator would attach a CSV import to whichever address looked
          // closest.
          // `type` too, so the flag-off result set is a superset of the flag-on one
          // rather than a subset of it — a narrower result is the failure mode this phase
          // exists to avoid. (The Property row here carries no tags to match on.)
          searchable: (property) => [
            property.id,
            property.address1,
            property.address2,
            property.addressCity,
            property.addressPostcode,
            property.customPropertyId,
            property.type,
          ],
        }
      ),
    {
      ...(options ?? {}),
      enabled: Boolean(organisationId) && (options?.enabled === undefined || options.enabled),
      keepPreviousData: true,
    }
  );
}

export function useQueryAllOrganisationsById(
  ids: string[]
) {
  return useAggregatedQueries<Organisation>(
    ids.map((id) => {
      const organisationId = id;
      return {
        queryKey: [BASE_QUERY_KEY, organisationId],
        queryFn: async () => getOrganisationById(organisationId),
      };
    })
  );
}


/** Sort fields the organisation search accepts. Mirrors the API's ORGANISATION_SORT_FIELDS. */
export const ORGANISATION_SORT_FIELDS = ['name', 'createdAt'] as const;

/** How a row is ordered by each of {@link ORGANISATION_SORT_FIELDS}. */
export const getOrganisationSortValue = sortValueFrom<Organisation>({
  name: (organisation) => organisation.name,
  createdAt: (organisation) => organisation.createdAt,
});

/**
 * ONE page of the organisation search, with the total.
 *
 * The Organisation List asked for `?name=%` and rendered the answer — which on this
 * platform is every organisation there is. The table then counted, searched and sorted
 * them in the browser, and the row it renders for the Customer column resolves a name per
 * row, so the download also decided how many of those lookups fired.
 */
export function getOrganisationsPage(
  request: PageRequest,
  queryClient?: QueryClient
): Promise<Page<Organisation>> {
  return getPage<Organisation>(
    'organisation',
    request,
    // `name: '%'` is the FLAG-OFF fallback. The legacy branch builds its where clause from
    // `name`, and a wildcard is what this screen has always sent, so with the flag off the
    // response is the same full list it serves today and getPage searches, sorts and
    // windows it. The paged branch applies it as well, where it matches everything.
    { name: '%' },
    {
      // Without these the search box would answer with the first N organisations while the
      // flag is off — not an error and not an empty state, a confident wrong answer.
      // `customerId` is a configured table column, so leaving it out makes pasted customer
      // UUIDs return an empty list even though the legacy table matched every column.
      searchable: (organisation) => [organisation.id, organisation.name, organisation.customerId],
      sortValue: getOrganisationSortValue,
    },
    queryClient,
    // The base key `useOrganisationsPage` queries under, so any invalidation that already
    // targets the organisation list drops the shared whole list with it.
    [BASE_QUERY_KEY, 'page']
  );
}

/** One page of organisations, as a query. */
export function useOrganisationsPage(request: PageRequest): UseQueryResult<Page<Organisation>, Error> {
  // Shares the FLAG-OFF whole-list download across pages, sorts and keystrokes.
  const queryClient = useQueryClient();
  return useQuery<Page<Organisation>, Error>(
    [BASE_QUERY_KEY, 'page', request],
    () => getOrganisationsPage(request, queryClient),
    {
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it. `isLoading` is false throughout under this — callers must read
      // `isFetching` to know a request is in flight.
      keepPreviousData: true,
    }
  );
}
